import { useEffect } from 'react';
import styled from 'styled-components';
import { color, typography } from 'styled-system';
import { themeGet } from '@styled-system/theme-get';
import { useTranslation } from 'react-i18next';

import i18n from '../../../../app/i18n';
import Sort from '../../../../shared/Sort';

import {
  ThContentWrapper,
  ColumnTitle,
} from '../../../../shared/TableComponents/StyledComponents';
import ManageUser from './ManageUser.jsx';
import Modal from '../../../../shared/Modal';
import { useInviteUserMutation } from '../shared/useInviteUserMutation';

const Table = styled.table`
  padding: 9px 24px 0;
  border-collapse: separate;
  box-shadow: inset 0 -1px var(--gray-3);
`;

const Tr = styled.tr`
  &:hover {
    background: white !important;
  }
`;

const Th = styled.th`
  color: ${themeGet('colors.black.100')};
  border-bottom: none !important;
  height: 32px;
  font-size: 14px;

  padding: 0 10px 10px 0;
  &:last-child {
    padding-right: 0;
  }
`;

const Td = styled.td`
  ${color};
  ${typography};
  font-size: 14px;
  height: 42px;

  border-top: 1px solid ${themeGet('colors.black.10')} !important;
  border-bottom: none !important;

  &:last-child {
    padding-right: 10px;
  }
`;

const UserRow = styled(Td)`
  cursor: pointer;
`;

const ThContent = styled(ThContentWrapper)`
  cursor: ${({ sortEnabled }) => (sortEnabled ? 'pointer' : 'default')};
`;

const InviteRow = styled(Td)`
  color: ${({ disabled }) =>
    disabled ? themeGet('colors.black.40') : themeGet('colors.primary.100')};
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  text-align: end;
`;

const tableColumns = [
  { name: 'admin', title: i18n.t('users:usersTable.admin'), sort: true },
  {
    name: 'employeeId',
    title: i18n.t('users:usersTable.employeeId'),
    sort: true,
  },
  { name: 'name', title: i18n.t('users:usersTable.name'), sort: true },
  { name: 'email', title: i18n.t('users:usersTable.email'), sort: true },
  { name: 'phone', title: i18n.t('users:usersTable.phone'), sort: true },
  {
    name: 'activated',
    title: i18n.t('users:usersTable.activated'),
    sort: true,
  },
  {
    name: 'sendInvite',
    title: i18n.t('users:usersTable.sendInvite'),
    sort: false,
  },
];

const openManageUserModal = user =>
  Modal({
    component: <ManageUser user={user} />,
    width: '394px',
    padding: '24px 24px',
  });

const TableHead = ({ sortingStatus, changeSort }) => (
  <thead>
    <Tr>
      {tableColumns.map(column => (
        <Th key={column.name}>
          <ThContent
            sortEnabled={column.sort}
            onClick={() => column.sort && changeSort(column.name)}
          >
            {column.sort && (
              <Sort sortingStatus={sortingStatus} columnName={column.name} />
            )}
            <ColumnTitle>{column.title}</ColumnTitle>
          </ThContent>
        </Th>
      ))}
    </Tr>
  </thead>
);

const isUserAdmin = user =>
  user.isAdmin
    ? i18n.t('users:usersTable.isAdmin')
    : i18n.t('users:usersTable.isNotAdmin');

const UsersTable = ({ data, sortingStatus, changeSort, openModal }) => {
  const { t } = useTranslation('users');
  const { mutate } = useInviteUserMutation();

  const inviteUser = (userId, hasContactInfo) => {
    if (hasContactInfo) {
      mutate(userId);
    }
  };

  useEffect(() => {
    const findUser = data.find(e => e.uuid === openModal);
    if (findUser) {
      openManageUserModal(findUser);
    }
  }, [data, openModal]);

  return (
    <Table>
      <colgroup>
        <col span="1" style={{ width: '10%' }}></col>
        <col span="1" style={{ width: '20%' }}></col>
        <col span="1" style={{ width: '15%' }}></col>
        <col span="1" style={{ width: '30%' }}></col>
        <col span="1" style={{ width: '30%' }}></col>
        <col span="1" style={{ width: '15%' }}></col>
      </colgroup>
      <TableHead sortingStatus={sortingStatus} changeSort={changeSort} />
      <tbody>
        {data.map(user => (
          <Tr key={user.uuid}>
            {user.markedForDeletion ? (
              <Td colSpan="6" color="error.default" textAlign="center">
                {i18n.t('users:usersTable.markedForDeletion', {
                  name: user.name,
                })}
              </Td>
            ) : (
              <>
                <Td color="black.100">{isUserAdmin(user)}</Td>
                <Td color="black.100">{user.employeeId}</Td>
                <UserRow
                  color="primary.100"
                  onClick={() => openManageUserModal(user)}
                >
                  {user.name}
                </UserRow>
                <Td color="black.100">{user.email}</Td>
                <Td color="black.100">{user.phoneNumber}</Td>
                <Td color="black.100">
                  {user.acceptedInvitationAt
                    ? t('usersTable.isActivated')
                    : t('usersTable.isNotActivated')}
                </Td>
                {(() => {
                  const hasContactInfo = user.email || user.phoneNumber;
                  return (
                    <InviteRow
                      data-testid={`${user.uuid}-invite`}
                      disabled={!hasContactInfo}
                      onClick={() => inviteUser(user.id, hasContactInfo)}
                    >
                      {t('usersTable.invite')}
                    </InviteRow>
                  );
                })()}
              </>
            )}
          </Tr>
        ))}
      </tbody>
    </Table>
  );
};

export default UsersTable;
